import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError, call } from "../api/client";
import { keys, useUsers } from "../api/queries";
import type { TermLanguage, UserView } from "../api/types";
import { ConfirmDialog, InputDialog } from "../components/Dialog";
import { ConnectedAppsSection } from "./AppPages";
import { useCurrentUser } from "./AuthPages";

export function UsersPage() {
  const users = useUsers();
  const client = useQueryClient();
  const [userName, setUserName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserView["role"]>("player");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [passwordFor, setPasswordFor] = useState<UserView | null>(null);
  const [deleting, setDeleting] = useState<UserView | null>(null);

  const change = async (action: Promise<{ response: Response }>) => {
    await call(action);
    await client.invalidateQueries({ queryKey: keys.users });
  };

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    setNotice(null);
    try {
      await action();
      await client.invalidateQueries({ queryKey: keys.users });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const create = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await call(api.POST("/api/users", { body: { userName, displayName, password, role } }));
      setUserName("");
      setDisplayName("");
      setPassword("");
    });
  };

  return (
    <div className="stack">
      <h2>Benutzer</h2>
      {error && <p className="error">{error}</p>}
      {notice && (
        <p className="muted" role="status">
          {notice}
        </p>
      )}
      <ul className="user-list">
        {users.data?.map((u) => (
          <li key={u.id} className="card row">
            <span>
              <strong>{u.displayName}</strong> <span className="muted">{u.userName}</span>
            </span>
            <select
              value={u.role}
              aria-label={`Rolle von ${u.displayName}`}
              onChange={(e) =>
                void run(() => call(api.PATCH("/api/users/{id}", { params: { path: { id: u.id } }, body: { role: e.target.value as UserView["role"] } })))
              }
            >
              <option value="player">Spieler</option>
              <option value="admin">Admin</option>
            </select>
            <button type="button" className="link" onClick={() => setPasswordFor(u)}>
              Passwort setzen
            </button>
            <button type="button" className="link danger" onClick={() => setDeleting(u)}>
              Löschen
            </button>
          </li>
        ))}
      </ul>
      <form className="card stack" onSubmit={create}>
        <h3>Neues Konto</h3>
        <label className="field">
          <span>Benutzername</span>
          <input value={userName} onChange={(e) => setUserName(e.target.value)} required />
        </label>
        <label className="field">
          <span>Anzeigename</span>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label className="field">
          <span>Passwort</span>
          <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
        </label>
        <label className="field">
          <span>Rolle</span>
          <select value={role} onChange={(e) => setRole(e.target.value as UserView["role"])}>
            <option value="player">Spieler</option>
            <option value="admin">Admin</option>
          </select>
        </label>
        <button type="submit" className="button primary">
          Anlegen
        </button>
      </form>

      {passwordFor && (
        <InputDialog
          title={`Passwort für ${passwordFor.displayName}`}
          label="Neues Passwort"
          type="password"
          autoComplete="new-password"
          action="Setzen"
          validate={(value) => (value.length < 8 ? "Das Passwort braucht mindestens 8 Zeichen." : null)}
          onClose={() => setPasswordFor(null)}
          onConfirm={async (password) => {
            await change(api.PATCH("/api/users/{id}", { params: { path: { id: passwordFor.id } }, body: { password } }));
            setNotice(`Passwort für ${passwordFor.displayName} gesetzt.`);
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title="Benutzer löschen"
          action="Löschen"
          danger
          onClose={() => setDeleting(null)}
          onConfirm={() => change(api.DELETE("/api/users/{id}", { params: { path: { id: deleting.id } } }))}
        >
          <p>
            {deleting.displayName} ({deleting.userName}) wird gelöscht und kann sich danach nicht mehr anmelden.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

export function ProfilePage() {
  const user = useCurrentUser();
  const client = useQueryClient();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [dossierMinutes, setDossierMinutes] = useState(String(user.dossierMinutes));
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>, done: string) => {
    setStatus(null);
    try {
      await action();
      await client.invalidateQueries({ queryKey: keys.auth });
      setStatus(done);
    } catch (e) {
      setStatus(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="stack">
      <h1>Profil</h1>
      {status && <p className="muted">{status}</p>}
      <form
        className="card stack"
        onSubmit={(e) => {
          e.preventDefault();
          void run(() => call(api.PATCH("/api/auth/me", { body: { displayName, dossierMinutes: Number(dossierMinutes) } })), "Gespeichert.");
        }}
      >
        <label className="field">
          <span>Anzeigename</span>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label className="field">
          <span>Fachbegriffe</span>
          <select
            value={user.termLanguage}
            onChange={(e) => void run(() => call(api.PATCH("/api/auth/me", { body: { termLanguage: e.target.value as TermLanguage } })), "Gespeichert.")}
          >
            <option value="de">deutsch</option>
            <option value="en">englisch</option>
            <option value="both">beide</option>
          </select>
        </label>
        <label className="field">
          <span>Dossierzeit in Minuten</span>
          <input type="number" min={1} max={240} step={1} value={dossierMinutes} onChange={(e) => setDossierMinutes(e.target.value)} />
        </label>
        <p className="muted small">So lange bleibt das Dossier einer Kampagne ohne Eingabe offen.</p>
        <button type="submit" className="button secondary">
          Speichern
        </button>
      </form>
      <form
        className="card stack"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            await call(api.POST("/api/auth/me/password", { body: { current, next } }));
            await client.invalidateQueries({ queryKey: keys.connectedApps });
          }, "Passwort geändert.");
        }}
      >
        <h3>Passwort ändern</h3>
        <label className="field">
          <span>Bisheriges Passwort</span>
          <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </label>
        <label className="field">
          <span>Neues Passwort</span>
          <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} />
        </label>
        <button type="submit" className="button secondary">
          Ändern
        </button>
        <p className="muted small">Danach sind alle verbundenen Anwendungen getrennt.</p>
      </form>
      <ConnectedAppsSection />
      <button
        type="button"
        className="button secondary"
        onClick={async () => {
          await call(api.POST("/api/auth/logout"));
          await client.invalidateQueries();
        }}
      >
        Abmelden
      </button>
    </div>
  );
}
